// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable @typescript-eslint/dot-notation */
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Analytics, getCurrentPlatformLink } from '@bpm-web-app/utils';
import { UserPlaylistCategory } from '@bpm-web-app/download-api-sdk';
import router from 'next/router';
import { Collapse } from 'react-collapse';
import { useUserPlaylistCategories } from '../shared/three-dots-sheet/useUserPlaylistCategories';
import styles from './nav-my-playlist-categories.module.css';
import navStyles from '../nav/nav.module.css';

import { ReactComponent as ActiveFolderIcon } from '../../assets/icons/folder-active.svg';
import { ReactComponent as FolderIcon } from '../../assets/icons/folder.svg';
import { ReactComponent as ChevronRight } from '../../assets/icons/chevron-right.svg';

import { CustomIcon } from '../shared/custom-icon/custom-icon';
// eslint-disable-next-line import/no-cycle
import NavMyPlaylistList from '../nav-my-playlist-list/nav-my-playlist-list';
import { NavContext } from '../nav/nav.context';
import { ContextMenuClickIgnoreClass, ContextMenuContext, ContextMenuItemProps } from '../shared';
import Droppable, { ALL_TYPES_NO_ARTIST } from '../droppable/droppable';

export type LocalSearchSort = 'most_recent' | 'title_asc' | 'title_desc';

export const LOCAL_SORT_OPTIONS = [
    { actionType: 'most_recent' as LocalSearchSort, label: 'Most Recent' },
    { actionType: 'title_asc' as LocalSearchSort, label: 'Title (a-z)' },
    { actionType: 'title_desc' as LocalSearchSort, label: 'Title (z-a)' },
];
export interface NavMyPlaylistsCategoriesProps {
    selectCategory: (id: number | null) => void;
    createNewPlaylist: () => void;
    sort: LocalSearchSort | undefined;
    isDragging?: boolean;
    onUpdateCategory: (id: number, currentName: string) => void;
    onDeleteCategory: (id: number) => void;
}

export function NavMyPlaylistsCategories({
    selectCategory,
    createNewPlaylist,
    sort,
    isDragging = false,
    onUpdateCategory,
    onDeleteCategory,
}: NavMyPlaylistsCategoriesProps) {
    const { userPlaylistCategories } = useUserPlaylistCategories(true);
    const { isNavOpen, openNav } = useContext(NavContext);
    const [playlistSort, setPlaylistSort] = useState<LocalSearchSort>('most_recent');

    const [userFolders, setUserFolders] = useState<UserPlaylistCategory[]>([]);

    const [showPlaylist, setShowPlaylist] = useState(false);
    const [currentFolder, setCurrentFolder] = useState<number | null>();
    const [isFolderOpened, setFolderOpened] = useState(false);
    const [folderWithPlaylists, setFolderWithPlaylists] = useState<{ [folderId: string]: string[] }>({});

    useEffect(() => {
        if (userPlaylistCategories && userPlaylistCategories.data) {
            setUserFolders(userPlaylistCategories.data);
        }
    }, [userPlaylistCategories]);

    const userPlaylistCategoriesList = useMemo(() => {
        if (sort) {
            switch (sort) {
                case 'most_recent':
                    return userFolders.sort((a, b) => {
                        if (a.slug === 'shared') {
                            return -1;
                        }
                        if (b.slug === 'shared') {
                            return 1;
                        }
                        return (a.created_at < b.created_at ? 1 : -1);
                    });
                case 'title_asc':
                    return userFolders.sort((a, b) => {
                        if (a.slug === 'shared') {
                            return -1;
                        }
                        if (b.slug === 'shared') {
                            return 1;
                        }
                        return (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1);
                    });
                case 'title_desc':
                    return userFolders.sort((a, b) => {
                        if (a.slug === 'shared') {
                            return -1;
                        }
                        if (b.slug === 'shared') {
                            return 1;
                        }
                        return (a.name.toLowerCase() < b.name.toLowerCase() ? 1 : -1);
                    });
                default:
                    return userFolders;
            }
        }

        return userFolders;
    }, [sort, userFolders]);

    const sharedFolderIcon = useMemo(() => <CustomIcon hasIconHover type="shared-folder" size="regular" color="boulder" />, []);

    const folderContainsActivePlaylist = useCallback((item: UserPlaylistCategory) => {
        // Check if folder is currently the route.
        if (router.query['categoryId'] === `${item.id}`) {
            return true;
        }

        // Check if playlist in folder is currently the route.
        if (router.query['playlistId'] && folderWithPlaylists && folderWithPlaylists[item.id]) {
            if (folderWithPlaylists[item.id].indexOf(router.query['playlistId'] as string) !== -1) return true;
        }
        return false;
    }, [folderWithPlaylists]);

    const renderFolderIcon = useCallback((item: UserPlaylistCategory) => {
        if (item.slug === 'shared') return sharedFolderIcon;
        return folderContainsActivePlaylist(item) ? <ActiveFolderIcon /> : <FolderIcon />;
    }, [folderContainsActivePlaylist, sharedFolderIcon]);

    const handleShowSubPlaylistFolders = useCallback(() => {
        if (!isNavOpen) openNav();
        setShowPlaylist(true);
    }, [isNavOpen, openNav]);

    const handleOpenAndCloseFolder = useCallback((id: number, onlyOpen = false) => {
        if (isFolderOpened && currentFolder === id && !onlyOpen) {
            setFolderOpened(false);
            setCurrentFolder(null);
            setShowPlaylist(false);
            selectCategory(null);
        } else {
            setFolderOpened(true);
            setShowPlaylist(true);
            setCurrentFolder(id);
            selectCategory(id);
        }
    }, [isFolderOpened, currentFolder, selectCategory]);

    const { openContextMenu, closeOptions, isOpen } = useContext(ContextMenuContext);

    const contextMenuOptions = useCallback((item: UserPlaylistCategory) => {
        const menuOptions: ContextMenuItemProps[] = [
            {
                leftIcon: <CustomIcon type="new-window-icon" hasIconHover />,
                label: 'Go to Folder',
                onClick: () => {
                    closeOptions();
                    router.push(getCurrentPlatformLink(`/my-playlists/${item.id}`));
                },
            },
            {
                label: 'Create Playlist',
                leftIcon: <CustomIcon type="add-circle-icon" hasIconHover />,
                onClick: () => {
                    closeOptions();
                    Analytics.trackClick('nav_bar', 'create_new_playlist', { location: 'nav_my_playlist' });
                    if (createNewPlaylist) createNewPlaylist();
                }
            },
            {
                label: 'Edit Folder Name',
                leftIcon: <CustomIcon type="edit-pencil-icon" hasIconHover />,
                onClick: () => {
                    closeOptions();
                    if (onUpdateCategory) onUpdateCategory(item.id, item.name);
                }
            },
            {
                label: 'Delete Folder',
                leftIcon: <CustomIcon type="delete-filled-icon" hasIconHover color="delete" />,
                onClick: () => {
                    closeOptions();
                    if (onDeleteCategory) onDeleteCategory(item.id);
                },
                type: 'delete-item'
            },
            {
                label: 'Sort By…',
                leftIcon: <CustomIcon type="sort-icon" hasIconHover />,
                items: LOCAL_SORT_OPTIONS.map((option) => {
                    return {
                        label: option.label,
                        onClick: () => {
                            closeOptions();
                            setPlaylistSort(option.actionType);
                            handleOpenAndCloseFolder(item.id, true);
                        }
                    };
                })
            }
        ];
        return item.slug === 'shared' ? [menuOptions[0], menuOptions[menuOptions.length - 1]] : menuOptions;
    }, [closeOptions, createNewPlaylist, onUpdateCategory, onDeleteCategory, handleOpenAndCloseFolder]);

    const handleOpenThreeDots = (e: React.MouseEvent<HTMLElement>, item: UserPlaylistCategory) => {
        const { left, top, width } = e.currentTarget.getBoundingClientRect();
        if (isOpen(item.id)) {
            closeOptions();
        } else {
            openContextMenu({ id: item.id, options: contextMenuOptions(item), left, top, renderLocationPosition: 'sidebar', align: 'right', buttonWidth: width });
        }
    };
    const isActive = useCallback((id: string | number) => isFolderOpened && currentFolder === id, [currentFolder, isFolderOpened]);

    const onUpdatePlaylistsPerCategory = useCallback((folderId: number, playlistIds: string[]) => {
        const tempCopyOfDict = folderWithPlaylists;
        tempCopyOfDict[`${folderId}`] = playlistIds;
        setFolderWithPlaylists(tempCopyOfDict);
    }, [folderWithPlaylists]);

    return (
        <>
            {userPlaylistCategoriesList.map((item) => (
                <Droppable
                    accept={ALL_TYPES_NO_ARTIST}
                    key={item.id}
                    onlyHover
                >
                    {(state) => (
                        <>
                            <button
                                type="button"
                                className={classNames(styles['nav-my-playlists-category-item'], {
                                    [styles['nav-my-playlists-category-item--is-dragging']]: isDragging,
                                    [styles['nav-my-playlists-category-item--is-dragging--over']]: state.isOver,
                                    [styles['nav-my-playlists-category-item--active']]: isActive(item.id),
                                    [styles['nav-my-playlists-category-item--active-open']]: folderContainsActivePlaylist(item)
                                })}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (item.playlist_count === 0) {
                                        router.push(getCurrentPlatformLink(`/my-playlists/${item.id}`));
                                    }

                                    handleShowSubPlaylistFolders();
                                    handleOpenAndCloseFolder(item.id);
                                    Analytics.trackClick('nav_bar', item.id.toString(), { location: 'nav_my_playlists_category' });
                                }}
                            >
                                <i className={`${styles['nav-my-playlists-category-item__button']}`}>
                                    {renderFolderIcon(item)}
                                </i>
                                <span>{item.name}</span>
                                <div className={classNames(styles['nav-my-playlists-category-item--three-dots'], {
                                    [styles['nav-my-playlists-category-item--three-dots--open']]: isFolderOpened || currentFolder === item.id
                                })}>
                                    <CustomIcon
                                        type="three-dots"
                                        hasIconHover
                                        className={ContextMenuClickIgnoreClass}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenThreeDots(e, item);
                                        }} />
                                </div>
                                {item.playlist_count > 0 ? (
                                    <button
                                        type="button"
                                        aria-label="Open or Close List"
                                        onClick={() => {
                                            handleOpenAndCloseFolder(item.id);
                                        }}
                                    >
                                        <ChevronRight
                                            className={classNames(styles['nav-my-playlists-category-item--chevron'], { [styles['nav-my-playlists-category-item--chevron--open']]: isFolderOpened && currentFolder === item.id })}
                                        />
                                    </button>) : null}
                            </button>
                            <Collapse isOpened={(showPlaylist && currentFolder === item.id) || state.isOver}>
                                <div className={classNames(navStyles['nav__dropdown-container'], { [navStyles['nav__dropdown-container--open']]: (showPlaylist && currentFolder === item.id) || state.isOver })}>
                                    <NavMyPlaylistList
                                        categoryId={item.id}
                                        sort={playlistSort}
                                        isDragging={isDragging}
                                        onGetPlaylistsPerCategory={onUpdatePlaylistsPerCategory}
                                    />
                                </div>
                            </Collapse>
                        </>
                    )}
                </Droppable>
            ))}
        </>
    );
}

export default NavMyPlaylistsCategories;
